import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 1-3: brick-heavy, floating platforms, high ? blocks, many pits
export function buildLevel3(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1; // ground occupies y=-1,-2; walking surface is y=0
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [45, 47],
    [63, 65],
    [82, 84],
    [106, 108],
    [120, 122],
    [138, 140],
    [162, 164]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  // main ground
  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // ---- early section: floating bricks + ? ----
  for (let x = 8; x <= 12; x++) setTile(x, 4, TileType.Brick);
  setTile(10, 4, TileType.Question, { kind: "mushroom" });

  setTile(16, 4, TileType.Question, { kind: "coin" });
  setTile(21, 4, TileType.Question, { kind: "coin" });
  setTile(25, 4, TileType.Question, { kind: "coin" });
  setTile(30, 5, TileType.Question, { kind: "mushroom" });

  // pipes
  const pipes: Array<[number, number]> = [
    [35, 2],
    [41, 3],
    [102, 3],
    [148, 2],
    [156, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // ---- after first pit: floating platform ladder ----
  for (let x = 49; x <= 52; x++) setTile(x, 4, TileType.Brick);
  for (let x = 53; x <= 56; x++) setTile(x, 7, TileType.Brick);
  for (let x = 57; x <= 60; x++) setTile(x, 4, TileType.Brick);
  setTile(59, 10, TileType.Question, { kind: "mushroom" });
  setTile(51, 4, TileType.Question, { kind: "coin" });
  setTile(55, 7, TileType.Question, { kind: "coin" });

  // ---- after second pit: staircase ----
  stair(68, 1);
  stair(69, 2);
  stair(70, 3);
  stair(71, 4);
  stair(72, 4);
  stair(73, 4);
  stair(74, 3);
  stair(75, 2);
  stair(76, 1);

  // ---- after third pit: brick arch ----
  for (let x = 88; x <= 98; x++) setTile(x, 4, TileType.Brick);
  setTile(90, 4, TileType.Question, { kind: "coin" });
  setTile(96, 4, TileType.Question, { kind: "coin" });
  for (let x = 90; x <= 96; x++) setTile(x, 8, TileType.Brick);
  setTile(92, 8, TileType.Question, { kind: "coin" });
  setTile(94, 8, TileType.Question, { kind: "mushroom" });

  // ---- after pit 4: brick row + high ? ----
  for (let x = 110; x <= 118; x++) setTile(x, 4, TileType.Brick);
  setTile(113, 4, TileType.Question, { kind: "coin" });
  setTile(116, 4, TileType.Question, { kind: "coin" });
  setTile(114, 9, TileType.Question, { kind: "coin" });
  setTile(115, 9, TileType.Question, { kind: "coin" });

  // ---- after pit 5: big brick structure ----
  for (let x = 124; x <= 135; x++) setTile(x, 4, TileType.Brick);
  setTile(126, 4, TileType.Question, { kind: "coin" });
  setTile(130, 4, TileType.Question, { kind: "coin" });
  for (let x = 127; x <= 132; x++) setTile(x, 7, TileType.Brick);
  setTile(129, 7, TileType.Question, { kind: "mushroom" });
  setTile(131, 7, TileType.Question, { kind: "coin" });

  // ---- after pit 6 ----
  stair(143, 1);
  stair(144, 2);
  stair(145, 3);
  stair(146, 4);
  stair(147, 4);
  stair(148, 4);
  stair(149, 3);
  stair(150, 2);
  stair(151, 1);

  for (let x = 153; x <= 154; x++) setTile(x, 4, TileType.Brick);
  for (let x = 166; x <= 175; x++) setTile(x, 4, TileType.Brick);
  setTile(168, 4, TileType.Question, { kind: "coin" });
  setTile(172, 4, TileType.Question, { kind: "coin" });
  setTile(170, 8, TileType.Question, { kind: "coin" });
  setTile(171, 8, TileType.Question, { kind: "coin" });
  setTile(173, 8, TileType.Question, { kind: "coin" });

  // final staircase up: 181..184 heights 1..4, flat 185..186 at 4, down 187..190
  stair(181, 1);
  stair(182, 2);
  stair(183, 3);
  stair(184, 4);
  stair(185, 4);
  stair(186, 4);
  stair(187, 4);
  stair(188, 3);
  stair(189, 2);
  stair(190, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block); // pole top ball

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 24 },
    { kind: "goomba", x: 27 },
    { kind: "paratroopa", x: 32 },
    { kind: "goomba", x: 38 },
    { kind: "goomba", x: 48 },
    { kind: "goomba", x: 53 },
    { kind: "paratroopa", x: 58 },
    { kind: "goomba", x: 67 },
    { kind: "goomba", x: 78 },
    { kind: "goomba", x: 81 },
    { kind: "goomba", x: 87 },
    { kind: "koopa", x: 92 },
    { kind: "goomba", x: 97 },
    { kind: "goomba", x: 99 },
    { kind: "goomba", x: 104 },
    { kind: "koopa", x: 109 },
    { kind: "goomba", x: 113 },
    { kind: "goomba", x: 116 },
    { kind: "goomba", x: 124 },
    { kind: "goomba", x: 127 },
    { kind: "koopa", x: 132 },
    { kind: "goomba", x: 135 },
    { kind: "goomba", x: 141 },
    { kind: "goomba", x: 145 },
    { kind: "koopa", x: 152 },
    { kind: "goomba", x: 158 },
    { kind: "goomba", x: 161 },
    { kind: "goomba", x: 167 },
    { kind: "koopa", x: 171 },
    { kind: "goomba", x: 175 },
    { kind: "goomba", x: 179 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
